import React, { useMemo } from 'react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, ReferenceLine,
} from 'recharts';
import { rangeStats, StatRows, fmtPct, pctColor } from './rangeStats';

const fmt = (v) => (v == null ? '—' : Number(v).toFixed(2));

function VIXStatsCard({ data, dateRange }) {
  const slice = useMemo(() => {
    const history = data?.history || [];
    if (!history.length) return [];
    if (!dateRange?.start || !dateRange?.end) return history;
    return history.filter((b) => b.date >= dateRange.start && b.date <= dateRange.end);
  }, [data, dateRange]);

  const stats = useMemo(() => rangeStats(slice, 'value', data?.current?.value), [slice, data]);

  if (!data || !data.current) {
    return (
      <div className="bg-gray-900 border border-gray-700 rounded-lg p-4 h-full">
        <h3 className="text-sm font-semibold text-gray-300">VIX</h3>
        <p className="text-gray-500 text-sm mt-2">Unavailable</p>
      </div>
    );
  }

  const { current } = data;

  return (
    <div className="bg-gray-900 border border-gray-700 rounded-lg p-4 h-full flex flex-col">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-baseline gap-3 flex-wrap">
          <h3 className="text-sm font-semibold text-gray-300">VIX · CBOE Volatility Index</h3>
          <span className="text-3xl font-bold text-white">{fmt(current.value)}</span>
          {stats && (
            <span className={`text-sm font-semibold ${pctColor(stats.changePct)}`}>
              Range {fmtPct(stats.changePct)}
            </span>
          )}
        </div>
        <span className="text-xs text-gray-500">EODHD</span>
      </div>
      <div className="flex flex-col sm:flex-row gap-4 flex-1">
        <div className="sm:w-44 flex-shrink-0">
          <StatRows stats={stats} fmtValue={fmt} showMedian />
        </div>
        <div className="flex-1 min-h-[160px]">
          <ResponsiveContainer width="100%" height="100%" minHeight={160}>
            <LineChart data={slice}>
              <XAxis dataKey="date" tick={{ fill: '#9ca3af', fontSize: 9 }} minTickGap={40} />
              <YAxis tick={{ fill: '#9ca3af', fontSize: 10 }} domain={['dataMin - 1', 'dataMax + 2']} />
              <Tooltip
                contentStyle={{ background: '#111827', border: '1px solid #374151', fontSize: 12 }}
                labelStyle={{ color: '#9ca3af' }}
                formatter={(v) => [fmt(v), 'VIX']}
              />
              {stats?.median != null && (
                <ReferenceLine y={stats.median} stroke="#f59e0b" strokeDasharray="3 3" label={{ value: `med ${fmt(stats.median)}`, fill: '#f59e0b', fontSize: 10, position: 'right' }} />
              )}
              {stats?.high != null && (
                <ReferenceLine y={stats.high} stroke="#dc2626" strokeDasharray="3 3" label={{ value: `high ${fmt(stats.high)}`, fill: '#dc2626', fontSize: 10, position: 'right' }} />
              )}
              <Line type="monotone" dataKey="value" stroke="#a78bfa" strokeWidth={1.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="text-[10px] text-gray-500 mt-2">
        EODHD VIX.INDX · dashed = range high &amp; median · as of {current.date}
      </div>
    </div>
  );
}

export default VIXStatsCard;
